import { BLANK_FUND_ID } from "../constants"
import {
  Amount,
  Config,
  ConfigForValidation,
  DateStampString,
  DraftFrequency,
  FrequencyType,
  Fund,
  Panes,
  State,
  StripePaymentMethod,
} from "../types"
import { moneyInputSanitize } from "source/giving/utils/money"
import { validate } from "./validations"

export type Action =
  | { type: "SET_FOCUSED_PANE"; payload: Panes }
  | { type: "ADD_DESIGNATION"; availableFundIds: Fund["id"][] }
  | {
      type: "UPDATE_DESIGNATION_FUND"
      fundId: string
      newFundId: string
      config: ConfigForValidation
    }
  | {
      type: "UPDATE_DESIGNATION_AMOUNT"
      fundId: string
      amount: Amount
      config: ConfigForValidation
    }
  | { type: "REMOVE_DESIGNATION"; fundId: string }
  | {
      type: "SET_FREQUENCY_TYPE"
      payload: FrequencyType
      frequencies: Config["frequencies"]
    }
  | { type: "CREATE_DRAFT_FREQUENCY" }
  | { type: "UPDATE_DRAFT_FREQUENCY"; frequency: DraftFrequency }
  | { type: "REFRESH_DRAFT_FREQUENCY_STARTING"; options: DateStampString[] }
  | { type: "PROMOTE_DRAFT_FREQUENCY" }
  | { type: "DELETE_DRAFT_FREQUENCY" }
  | { type: "START_LIVE_VALIDATION"; config: ConfigForValidation }
  | { type: "INPUT_BLURRED"; config: ConfigForValidation }
  | {
      type: "SET_STRIPE_PAYMENT_METHOD"
      stripePaymentMethod: StripePaymentMethod
    }
  | {
      type: "SET_SAVE_PAYMENT_METHOD_CHECKED"
      savePaymentMethodChecked: boolean
    }
  | { type: "SET_FEE_COVERED"; feeCovered: boolean }
  | { type: "START_POLLING_DONATION_STATUS"; donationId: string }
  | { type: "STOP_POLLING_DONATION_STATUS" }

export function reducer(state: State, action: Action): State {
  if (process.env.NODE_ENV === "development") {
    console.debug("[DonationFormReducer]", { action, state })
  }

  switch (action.type) {
    case "SET_FOCUSED_PANE": {
      return { ...state, focusedPane: action.payload }
    }
    case "ADD_DESIGNATION": {
      const alreadyHasBlankDesignation = state.designations.some(
        (d) => d.fundId === BLANK_FUND_ID,
      )
      if (alreadyHasBlankDesignation) return state

      const fundId =
        action.availableFundIds.length === 1
          ? action.availableFundIds[0]
          : BLANK_FUND_ID

      const designations = [...state.designations, { fundId, amount: "" }]
      return {
        ...state,
        designations,
      }
    }
    case "UPDATE_DESIGNATION_FUND": {
      const designations = state.designations.map((designation) =>
        designation.fundId === action.fundId
          ? {
              ...designation,
              fundId: action.newFundId,
            }
          : designation,
      )
      const newState = { ...state, designations }
      if (state.liveValidate) {
        return {
          ...newState,
          errors: validate(newState, action.config),
        }
      } else {
        return newState
      }
    }
    case "UPDATE_DESIGNATION_AMOUNT": {
      const designations = state.designations.map((designation) =>
        designation.fundId === action.fundId
          ? {
              ...designation,
              amount: moneyInputSanitize(action.amount),
            }
          : designation,
      )
      const newState = { ...state, designations }
      if (state.liveValidate) {
        return {
          ...newState,
          errors: validate(newState, action.config),
        }
      } else {
        return newState
      }
    }
    case "REMOVE_DESIGNATION": {
      const designations = state.designations.filter(
        (d) => d.fundId !== action.fundId,
      )
      return { ...state, designations }
    }
    case "SET_FREQUENCY_TYPE": {
      const type = action.payload
      const frequency = action.frequencies.find((f) => f.type === type)
      if (!frequency) {
        throw new Error(
          `Frequency of type "${type}" not found in action.frequencies`,
        )
      }
      const { draftFrequency, ...otherState } = state
      return { ...otherState, frequency }
    }
    case "CREATE_DRAFT_FREQUENCY": {
      if (state.frequency.type === "once") return state
      const { frequency: draftFrequency } = state
      return { ...state, draftFrequency }
    }
    case "UPDATE_DRAFT_FREQUENCY": {
      const { draftFrequency, ...otherState } = state
      return { ...otherState, draftFrequency: action.frequency }
    }
    case "REFRESH_DRAFT_FREQUENCY_STARTING": {
      if (!state.draftFrequency) {
        return state
      }
      if (action.options.includes(state.draftFrequency.starting)) {
        return state
      } else {
        const starting = action.options[0]
        const draftFrequency = { ...state.draftFrequency, starting }
        return { ...state, draftFrequency }
      }
    }
    case "PROMOTE_DRAFT_FREQUENCY": {
      const { draftFrequency, frequency, ...otherState } = state
      if (!draftFrequency) return state
      return { ...otherState, frequency: draftFrequency }
    }
    case "DELETE_DRAFT_FREQUENCY": {
      const { draftFrequency, ...otherState } = state
      return otherState
    }
    case "START_LIVE_VALIDATION": {
      return {
        ...state,
        liveValidate: true,
        errors: validate(state, action.config),
      }
    }
    case "INPUT_BLURRED": {
      if (state.liveValidate) {
        return {
          ...state,
          errors: validate(state, action.config),
        }
      } else {
        return state
      }
    }
    case "SET_STRIPE_PAYMENT_METHOD": {
      return {
        ...state,
        stripePaymentMethod: action.stripePaymentMethod,
      }
    }
    case "SET_SAVE_PAYMENT_METHOD_CHECKED": {
      return {
        ...state,
        savePaymentMethodChecked: action.savePaymentMethodChecked,
      }
    }
    case "SET_FEE_COVERED": {
      return { ...state, feeCovered: action.feeCovered }
    }
    case "START_POLLING_DONATION_STATUS": {
      return {
        ...state,
        pollingDonationStatusId: action.donationId,
      }
    }
    case "STOP_POLLING_DONATION_STATUS": {
      const { pollingDonationStatusId, ...otherState } = state
      return otherState
    }
    default:
      return state
  }
}

export type Dispatch = React.Dispatch<Action>
